import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import PowerIcon from "@mui/icons-material/Power";
import { useAuthApi } from "../hooks/useAuthApi";
import { MGMT } from "../env";
import type { ChargerModel } from "../types";
import { useToast } from "../toast";
import ChargerModelDialog from "../dialogs/ChargerModelDialog";
import ChargerModelConnectorsDialog from "../dialogs/ChargerModelConnectorsDialog";
import ConfirmDialog from "../dialogs/ConfirmDialog";

export default function ChargerModels() {
  const api = useAuthApi();
  const [items, setItems] = useState<ChargerModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<ChargerModel | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<ChargerModel | null>(null);
  const [managingConnectors, setManagingConnectors] = useState<ChargerModel | null>(null);
  const [search, setSearch] = useState("");
  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api<ChargerModel[]>(`${MGMT}/charger-models`);
      setItems(data ?? []);
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await api(`${MGMT}/charger-models/${deleting.id}`, { method: "DELETE" });
      toast.show("Charger model deleted");
      setDeleting(null);
      load();
    } catch (e) {
      toast.show((e as Error).message, "error");
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((m) =>
      [m.vendor, m.model_name, m.description].filter(Boolean).join(" ").toLowerCase().includes(q)
    );
  }, [items, search]);

  const hasFilters = search.trim().length > 0;

  return (
    <>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)}>
          Add Model
        </Button>
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Stack>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 2 }} alignItems="center">
        <TextField
          size="small"
          placeholder="Filter charger models…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260 }}
          InputProps={{
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch("")}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        {hasFilters && <Button size="small" onClick={() => setSearch("")}>Clear filters</Button>}
      </Stack>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Vendor</TableCell>
              <TableCell>Model Name</TableCell>
              <TableCell>Connectors</TableCell>
              <TableCell align="right">Max Power (kW)</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}
            {!loading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Box sx={{ py: 3 }}>
                    <Typography color="text.secondary">
                      {hasFilters
                        ? "No charger models match the current filters."
                        : "No charger models yet. Add one to use during onboarding."}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {filtered.map((m) => (
              <TableRow key={m.id} hover>
                <TableCell sx={{ fontWeight: 600 }}>{m.vendor}</TableCell>
                <TableCell>{m.model_name}</TableCell>
                <TableCell>
                  {m.connectors && m.connectors.length > 0
                    ? m.connectors.map((c) => `#${c.connector_number} ${c.connector_type}${c.max_power_kw != null ? ` (${c.max_power_kw} kW)` : ""}`).join(", ")
                    : "—"}
                </TableCell>
                <TableCell align="right">
                  {m.max_power_kw != null ? m.max_power_kw : "—"}
                </TableCell>
                <TableCell sx={{ color: "text.secondary", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {m.description ?? "—"}
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" title="Manage connectors" onClick={() => setManagingConnectors(m)}>
                    <PowerIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setEditing(m)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => setDeleting(m)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <ChargerModelDialog
        open={creating || editing !== null}
        model={editing}
        onClose={() => { setCreating(false); setEditing(null); }}
        onSaved={() => { setCreating(false); setEditing(null); load(); }}
      />

      <ChargerModelConnectorsDialog
        open={managingConnectors !== null}
        model={managingConnectors}
        onClose={() => { setManagingConnectors(null); load(); }}
      />

      <ConfirmDialog
        open={deleting !== null}
        title="Delete charger model?"
        message={`Delete "${deleting?.vendor} ${deleting?.model_name}"? Charger configs referencing this model will lose their model link.`}
        onCancel={() => setDeleting(null)}
        onConfirm={handleDelete}
      />
    </>
  );
}
